import { useCallback, useEffect, useLayoutEffect, useRef, useState, type FocusEvent, type KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { AlignLeft } from 'lucide-react'
import { cn } from '@/lib/utils'
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion'
import { isFoldCard, useTickerStore, type TickerCard } from '@/app/store/ticker'
import { dwellFor, SLIDE_EASE, SLIDE_MS } from '../kinds'
import { KIND_PALETTE } from '../palette'
import { useTickerPermission } from '../hooks'
import { KindAvatar, KindChip, SampleChip } from './KindChip'

/**
 * The conveyor: one card on the taskbar at a time, as the prototype.
 *
 * - A card slides in from above while the one before it slides out below, together, over 520 ms on
 *   cubic-bezier(.33,0,.2,1). The first card in and the last card out use the same motion.
 * - Each card dwells for its time under the user's "Time on screen" setting - whole seconds, 3 s
 *   (task and object complete) or 5 s (the rest) at normal; see DWELL_MS - with a 1 px bar counting
 *   the dwell down. When the dwell ends the next queued card replaces it; with nothing queued the
 *   card slides out and the bar is empty.
 * - The start position is committed with a forced reflow before every slide, so the browser
 *   animates from it rather than jumping to the end.
 * - With prefers-reduced-motion the cards cross-fade in place instead of sliding.
 * - The slot clips the cards (overflow hidden, the bar's height), so a card never overlaps the page.
 * - While the pointer is over the bar, or a card has keyboard focus, the dwell PAUSES (the countdown
 *   bar freezes where it is) and resumes when both have left. A card is never pulled out from under
 *   a click, and a focused card never leaves, so focus is never dropped to <body>.
 * - A backlog is one card: "N messages while you were away · See all", which opens the panel
 *   (the store folds anything past a handful; see BACKLOG_MAX).
 */

type Phase = 'in' | 'out'
interface Card {
    item: TickerCard
    phase: Phase
}

const raf = (fn: () => void) =>
    typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function'
        ? window.requestAnimationFrame(fn)
        : (setTimeout(fn, 16) as unknown as number)

/** The prototype's double rAF: the transition applies from the frame after the reflow. */
const nextFrame = (fn: () => void) => raf(() => raf(fn))

/**
 * Set a card's transition so it cannot be overruled. The card is role="button", and globals.css makes
 * every button's transitions instant with `transition-duration: 0ms !important` (instant feedback on
 * clicks). A stylesheet !important beats a plain inline style, so the 520 ms slide ran in 0 ms and
 * cards jumped into place instead of travelling down the bar (Andrew, 29 Sep 2026: "appearing one
 * after another ... it was top down as if they are on a belt"). An inline !important beats it.
 */
function setMotion(el: HTMLElement, transition: string) {
    el.style.setProperty('transition', transition, 'important')
}

/** -1 above the bar, 0 centred, 1 below. Faded rather than moved under reduced motion. */
function place(el: HTMLElement, y: -1 | 0 | 1, reduced: boolean) {
    if (reduced) {
        el.style.transform = 'translateY(-50%)'
        el.style.opacity = y === 0 ? '1' : '0'
        return
    }
    const bar = el.parentElement?.offsetHeight || 56
    el.style.opacity = '1'
    el.style.transform = `translateY(calc(-50% + ${y * bar}px))`
}

/** A fold card dwells as long as an update: it is read, not glanced at. */
const dwellOf = (card: TickerCard) =>
    dwellFor(isFoldCard(card) ? 'management_update' : card.kind, useTickerStore.getState().prefs?.dwell)

function ConveyorCard({
    card,
    reduced,
    registerBar,
}: {
    card: Card
    reduced: boolean
    registerBar: (id: number, el: HTMLDivElement | null) => void
}) {
    const { t } = useTranslation()
    const ref = useRef<HTMLDivElement>(null)
    const mounted = useRef(false)
    const openPanel = useTickerStore((s) => s.openPanel)
    const { item, phase } = card

    useLayoutEffect(() => {
        const el = ref.current
        if (!el) return
        if (!mounted.current) {
            mounted.current = true
            // Start above the bar (or invisible), with no transition, and force a reflow so the
            // browser commits that position before the slide begins.
            setMotion(el, 'none')
            place(el, -1, reduced)
            void el.offsetHeight
        }
        setMotion(el, reduced ? `opacity ${SLIDE_MS}ms ease` : `transform ${SLIDE_MS}ms ${SLIDE_EASE}`)
        el.dataset.motion = reduced ? 'fade' : 'slide'
        const target = phase === 'in' ? 0 : 1
        nextFrame(() => {
            if (ref.current) place(ref.current, target, reduced)
        })
    }, [phase, reduced])

    const fold = isFoldCard(item) ? item : null
    const message = isFoldCard(item) ? null : item
    const title = message ? message.title : t(fold?.reason === 'away' ? 'ticker.foldAway' : 'ticker.foldMore', { count: fold?.count })
    const summary = message ? message.summary : t('ticker.foldSeeAll')
    // A fold card opens the panel on everything; a message card on its own row.
    const open = () => openPanel(message ? message.id : null)
    const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            open()
        }
    }
    const pal = message ? KIND_PALETTE[message.kind] : null

    return (
        <div
            ref={ref}
            role="button"
            tabIndex={phase === 'in' ? 0 : -1}
            aria-label={t('ticker.cardLabel', { title, summary })}
            data-ticker-card={item.id}
            data-fold={fold ? fold.reason : undefined}
            data-phase={phase}
            onClick={open}
            onKeyDown={onKeyDown}
            className={cn(
                'absolute left-3.5 right-3.5 top-1/2 flex cursor-pointer items-center gap-[11px] overflow-hidden rounded-[10px] border-[0.5px] bg-background px-3 py-[7px] will-change-transform',
                'shadow-[0_1px_2px_rgba(16,49,77,.05),0_6px_18px_rgba(16,49,77,.08)] dark:shadow-[0_1px_2px_rgba(0,0,0,.3),0_6px_18px_rgba(0,0,0,.35)]',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                pal ? pal.border : 'border-primary/25',
            )}
        >
            <span aria-hidden className={cn('pointer-events-none absolute inset-0', pal ? pal.fill : 'bg-primary/[.04]')} />
            {!message ? (
                <span
                    aria-hidden
                    className="relative flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"
                >
                    <AlignLeft className="h-3.5 w-3.5" strokeWidth={2.2} />
                </span>
            ) : (
                <>
                    <KindChip kind={message.kind} size="card" />
                    <KindAvatar kind={message.kind} initials={message.initials} size="card" />
                </>
            )}
            <div className="relative min-w-0 flex-1 leading-[1.3]">
                <div className="truncate text-[13px] font-semibold text-foreground">{title}</div>
                <div className={cn('mt-px truncate text-[11px]', fold ? 'font-semibold text-primary' : 'text-muted-foreground')}>{summary}</div>
            </div>
            {message?.sample && <SampleChip size="card" />}
            <div
                ref={(el) => registerBar(item.id, el)}
                aria-hidden
                data-countdown
                className={cn('absolute bottom-0 left-0 h-px rounded-r-[1px] opacity-40', pal ? pal.bar : 'bg-primary')}
                style={{ width: '100%' }}
            />
        </div>
    )
}

/** The card on the bar and its dwell: running (timer set) or paused (remaining held). */
interface Dwell {
    id: number
    total: number
    remaining: number
    endsAt: number
    timer: ReturnType<typeof setTimeout> | null
}

function Conveyor() {
    const { t } = useTranslation()
    const reduced = usePrefersReducedMotion()
    const waiting = useTickerStore((s) => s.queue.length + (s.fold ? 1 : 0))
    const [cards, setCards] = useState<Card[]>([])
    const engine = useRef({
        current: null as TickerCard | null,
        animating: false,
        timers: new Set<ReturnType<typeof setTimeout>>(),
        dwell: null as Dwell | null,
        hovered: false,
        focused: false,
    })
    const bars = useRef(new Map<number, HTMLDivElement>())

    const later = useCallback((ms: number, fn: () => void) => {
        const timer = setTimeout(() => {
            engine.current.timers.delete(timer)
            fn()
        }, ms)
        engine.current.timers.add(timer)
        return timer
    }, [])

    const registerBar = useCallback((id: number, el: HTMLDivElement | null) => {
        if (el) bars.current.set(id, el)
        else bars.current.delete(id)
    }, [])

    /** Run the countdown bar from `fromPct` of its width to empty over `ms`. */
    const runCountdown = useCallback((id: number, ms: number, fromPct = 100) => {
        const bar = bars.current.get(id)
        if (!bar) return
        bar.style.transition = 'none'
        bar.style.width = `${fromPct}%`
        void bar.offsetHeight
        raf(() => {
            bar.style.transition = `width ${ms}ms linear`
            bar.style.width = '0%'
        })
    }, [])

    const freezeCountdown = useCallback((id: number, pct: number) => {
        const bar = bars.current.get(id)
        if (!bar) return
        bar.style.transition = 'none'
        bar.style.width = `${pct}%`
    }, [])

    // advance and the dwell's end call each other; the ref keeps the latest advance.
    const advanceRef = useRef<() => void>(() => {})

    const dwellEnded = useCallback(() => {
        const eng = engine.current
        eng.dwell = null
        if (useTickerStore.getState().queue.length || useTickerStore.getState().fold) {
            advanceRef.current()
            return
        }
        if (!eng.current || eng.animating) return
        // Nothing queued: the last card leaves the way every other card does.
        eng.animating = true
        const off = eng.current
        eng.current = null
        setCards((prev) => prev.map((c) => (c.item.id === off.id ? { ...c, phase: 'out' } : c)))
        later(SLIDE_MS, () => {
            setCards((prev) => prev.filter((c) => c.item.id !== off.id))
            eng.animating = false
            const s = useTickerStore.getState()
            if (s.queue.length || s.fold) advanceRef.current()
        })
    }, [later])

    const hold = useCallback(
        (item: TickerCard) => {
            // Read at the moment the card lands, so a change of setting applies from the next card.
            const total = dwellOf(item)
            const eng = engine.current
            const dwell: Dwell = { id: item.id, total, remaining: total, endsAt: Date.now() + total, timer: null }
            eng.dwell = dwell
            if (eng.hovered || eng.focused) {
                // Landed under the pointer or the focus: it waits, full, until they leave.
                freezeCountdown(item.id, 100)
                return
            }
            runCountdown(item.id, total)
            dwell.timer = later(total, dwellEnded)
        },
        [dwellEnded, freezeCountdown, later, runCountdown],
    )

    const pause = useCallback(() => {
        const dwell = engine.current.dwell
        if (!dwell || !dwell.timer) return
        clearTimeout(dwell.timer)
        engine.current.timers.delete(dwell.timer)
        dwell.timer = null
        dwell.remaining = Math.max(0, dwell.endsAt - Date.now())
        freezeCountdown(dwell.id, (dwell.remaining / dwell.total) * 100)
    }, [freezeCountdown])

    const resume = useCallback(() => {
        const dwell = engine.current.dwell
        if (!dwell || dwell.timer || engine.current.hovered || engine.current.focused) return
        dwell.endsAt = Date.now() + dwell.remaining
        runCountdown(dwell.id, dwell.remaining, (dwell.remaining / dwell.total) * 100)
        dwell.timer = later(dwell.remaining, dwellEnded)
    }, [dwellEnded, later, runCountdown])

    const advance = useCallback(() => {
        const eng = engine.current
        if (eng.animating) return
        const item = useTickerStore.getState().shift()
        if (!item) return
        eng.animating = true
        const out = eng.current
        eng.current = item
        // The incoming and outgoing cards move in the same frame.
        setCards((prev) => [
            ...prev.filter((c) => out && c.item.id === out.id).map((c) => ({ ...c, phase: 'out' as const })),
            { item, phase: 'in' },
        ])
        later(SLIDE_MS, () => {
            setCards((prev) => prev.filter((c) => c.item.id === item.id))
            eng.animating = false
            hold(item)
        })
    }, [hold, later])
    advanceRef.current = advance

    useEffect(() => {
        const eng = engine.current
        if (waiting > 0 && !eng.current && !eng.animating) advance()
    }, [waiting, advance])

    useEffect(() => {
        const eng = engine.current
        return () => {
            eng.timers.forEach(clearTimeout)
            eng.timers.clear()
            eng.current = null
            eng.animating = false
            eng.dwell = null
        }
    }, [])

    const onMouseEnter = () => {
        engine.current.hovered = true
        pause()
    }
    const onMouseLeave = () => {
        engine.current.hovered = false
        resume()
    }
    const onFocus = () => {
        engine.current.focused = true
        pause()
    }
    const onBlur = (e: FocusEvent<HTMLDivElement>) => {
        // Focus moving between cards inside the bar is still focus on the bar.
        if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) return
        engine.current.focused = false
        resume()
    }

    return (
        <div
            role="region"
            aria-label={t('ticker.regionLabel')}
            data-testid="taskbar-ticker"
            className="relative mr-4 min-w-0 flex-1 self-stretch overflow-hidden"
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onFocus={onFocus}
            onBlur={onBlur}
        >
            {cards.map((card) => (
                <ConveyorCard key={card.item.id} card={card} reduced={reduced} registerBar={registerBar} />
            ))}
        </div>
    )
}

/**
 * The taskbar conveyor. Renders nothing without `ticker.view`, with the ticker switched off, or
 * with no recovery sets chosen; unmounting it stops every timer.
 */
export function TaskbarTicker() {
    const allowed = useTickerPermission()
    const prefs = useTickerStore((s) => s.prefs)
    if (!allowed || !prefs?.enabled || prefs.recoverySetIds.length === 0) return null
    return <Conveyor />
}
